import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { State } from "../store/interfaces";
import {
  createLeadAction,
  createLeadRequestAction,
  createLeadErrorAction,
  updateLeadAction,
  updateLeadRequestAction,
  updateLeadErrorAction,
  getLeadSuccessAction,
  getLeadRequestAction,
  getLeadErrorAction,
  getLeadActionSuccess,
  getLeadActionRequest,
  getLeadActionError,
  clearLeadAction,
} from "../store/reducers/lead";
import { StatusCodes } from "http-status-codes";
import { Company, CreateInterestedProject, Lead, LeadService } from "../api";
import {
  PartnerInfo,
  Customer,
  VehicleUserDetails,
  CreateLead,
  LeadDemographic,
  CreateVehicle,
  LeadQualityChange,
  ILeadBulkAssign,
  CreateScheduleATestDrive,
  CreateShowroomVisit,
  CreateRequestACallbackt,
  Tag,
  IMarkAsLost,
  AssigneePerson
} from "../api/models";

export const useLead = (leadService: LeadService) => {
  const leadState = useSelector((state: State) => state.lead);
  const dispatch = useDispatch();
  const [leadError, setLeadError] = useState<string | undefined>();
  const [updating, setUpdating] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);

  const createLead = async (
    data: CreateLead,
    onSuccess?: (id?: string) => void,
    onError?: (e: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(createLeadRequestAction());
    try {
      const response = await leadService.createLead(data);

      if (response.status === StatusCodes.OK) {
        dispatch(createLeadAction({ leadDetails: response.data }));
        if (onSuccess) onSuccess(response?.data?._id);
      } else {
        dispatch(createLeadErrorAction());
      }
    } catch (error) {
      onError?.(error);
      if (
        error?.response?.data?.data?.message &&
        Array.isArray(error?.response?.data?.data?.message)
      ) {
        setLeadError(error?.response?.data?.data?.message.join());
      } else {
        setLeadError(error?.response?.data?.message);
      }

      dispatch(createLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateSocialMediaDetails = async (
    data: Customer,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateSocialMediaDetails(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              customer: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateCompanySocialMediaDetails = async (
    data: Company,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateCompanySocialMediaDetails(
        data,
        id
      );

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              company: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updatePreferredTimes = async (
    data: Customer,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updatePreferredTimes(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              customer: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateVehicleDetails = async (
    data: VehicleUserDetails[],
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateVehicleDetails(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              customer: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updatePartnerInfo = async (
    data: PartnerInfo,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updatePartnerInfo(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              customer: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateBuyingInfluences = async (
    data: any,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateBuyingInfluences(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateWorkExperiences = async (
    data: Customer,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateWorkExperiences(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              customer: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const bulkAssign = async (data: ILeadBulkAssign, onSuccess?: () => void) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(createLeadRequestAction());
    try {
      const response = await leadService.bulkAssign(data);

      if (response.status === StatusCodes.OK) {
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      toastr.error("Error", "Unable to assign values for leads");
    }
    setUpdating(false);
  };

  const resetWorkExperiences = async (id?: string, onSuccess?: () => void) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.resetWorkExperiences(id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              customer: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateDemographics = async (
    data: LeadDemographic,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateDemographics(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              customer: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      if (error?.response?.data?.data?.message) {
        toastr.error("Error", error?.response?.data?.data?.message.join());
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const getLead = async (id?: string, onSuccess?: () => void) => {
    setLeadError(undefined);
    setFetching(true);
    dispatch(getLeadRequestAction());
    try {
      const response = await leadService.getLead(id);

      if (response.status === StatusCodes.OK) {
        dispatch(getLeadSuccessAction({ leadDetails: response.data }));
        if (onSuccess) onSuccess();
      } else {
        dispatch(getLeadErrorAction());
      }
    } catch (error) {
      dispatch(getLeadErrorAction());
    }
    setFetching(false);
  };

  const updateCustomerAnswers = async (
    data: any,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateCustomerAnswers(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      if (error?.response?.data?.data?.message) {
        toastr.error("Error", "All answer should not be empty");
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
    }
    setUpdating(false);
  };

  const updateDocumentsDetails = async (
    data: Customer,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateDocumentsDetails(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              customer: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateCustomerDetails = async (
    data: Customer,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      dispatch(
        updateLeadAction({
          leadDetails: {
            ...leadState.leadDetails,
            customer: data,
          },
        })
      );
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const assignCrmAgent = async (
    crmAgentId: string,
    id?: string,
    crmChangeReasonId?: string,
    onSuccess?: () => void,
    onError?: (e: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.assignCrmAgent(
        crmAgentId,
        id,
        crmChangeReasonId
      );

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        onError?.(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      onError?.(error);
    }
    setUpdating(false);
  };

  const assignLeadAssigneeHead = async (
    LeadAssigneeHeadId: string,
    id?: string,
    LeadAssigneeHeadChangeReasonId?: string,
    onSuccess?: () => void,
    onError?: (e: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.assignLeadAssigneeHead(
        LeadAssigneeHeadId,
        id,
        LeadAssigneeHeadChangeReasonId
      );

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        onError?.(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      onError?.(error);
    }
    setUpdating(false);
  };

  const updateAssignee = async (
    id: string,
    {leadManagerId, groupManagerId, telecallerId, intercityUserId ,  type}: AssigneePerson,
    LeadAssigneeHeadChangeReasonId?: string,
    onSuccess?: () => void,
    onError?: (e: any) => void,
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateAssignee(
        id,
        {leadManagerId, LeadAssigneeHeadChangeReasonId, groupManagerId,telecallerId, intercityUserId , type},
      );

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        onError?.(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      onError?.(error);
    }
    setUpdating(false);
  };

  const assignSalesPerson = async (
    data: {
      salesPersonId: string;
      customerReqSalesPerson: boolean;
      salesChangeReasonId?: string;
    },
    id?: string,
    onSuccess?: () => void,
    onError?: (e: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.assignSalesPerson(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        onError?.(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      onError?.(error);
    }
    setUpdating(false);
  };

  const addVehicle = async (
    data: CreateVehicle,
    id?: string,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.addVehicle(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        if (onError) onError();
      }
    } catch (error) {
      if (error?.response?.data?.data?.description) {
        toastr.error("Error", error?.response?.data?.data?.description);
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
      dispatch(updateLeadErrorAction());
      if (onError) onError();
    }
    setUpdating(false);
  };

  const updateVehicle = async (
    data: CreateVehicle,
    id?: string,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateVehicle(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        if (onError) onError();
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      if (error?.response?.data?.data?.description) {
        toastr.error("Error", error?.response?.data?.data?.description);
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
      dispatch(updateLeadErrorAction());
      if (onError) onError();
    }
    setUpdating(false);
  };

  const addInterestedProject = async (
    data: CreateInterestedProject,
    id?: string,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.addInterestedProject(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        if (onError) onError();
      }
    } catch (error) {
      if (error?.response?.data?.data?.description) {
        toastr.error("Error", error?.response?.data?.data?.description);
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
      dispatch(updateLeadErrorAction());
      if (onError) onError();
    }
    setUpdating(false);
  };

  const updateInterestedProject = async (
    data: CreateInterestedProject,
    id?: string,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateInterestedProject(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        if (onError) onError();
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      if (error?.response?.data?.data?.description) {
        toastr.error("Error", error?.response?.data?.data?.description);
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
      dispatch(updateLeadErrorAction());
      if (onError) onError();
    }
    setUpdating(false);
  };

  const setVehicleAsPrimary = async (
    id?: string,
    vehicleId?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.setVehicleAsPrimary(id, vehicleId);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      if (error?.response?.data?.data?.description) {
        toastr.error("Error", error?.response?.data?.data?.description);
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateLeadQuality = async (
    data: LeadQualityChange,
    id?: string,
    onSuccess?: () => void,
    onError?: (error: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateLeadQuality(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        if (onError) onError(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      if (onError) onError(error);
    }
    setUpdating(false);
  };

  const scheduleTestDrive = async (
    data: CreateScheduleATestDrive,
    leadId?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);

    try {
      const response = await leadService.scheduleTestDrive(data, leadId);

      if (response.status === StatusCodes.OK) {
        dispatch(getLeadActionSuccess({ leadActions: leadState?.leadActions }));
        if (onSuccess) onSuccess();
      } else {
        dispatch(getLeadActionError());
      }
    } catch (error) {
      toastr.error(
        "Error",
        error.response?.data?.data?.description ??
          error?.response?.data?.message ??
          "Something went wrong while schedule the test drive"
      );
      dispatch(getLeadActionError());
    }
    setUpdating(false);
  };

  const scheduleShowroomVisit = async (
    data: CreateShowroomVisit,
    leadId?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);

    try {
      const response = await leadService.scheduleShowroomVisit(data, leadId);

      if (response.status === StatusCodes.OK) {
        dispatch(getLeadActionSuccess({ leadActions: leadState?.leadActions }));
        if (onSuccess) onSuccess();
      } else {
        dispatch(getLeadActionError());
      }
    } catch (error) {
      toastr.error(
        "Error",
        error.response?.data?.data?.description ??
          error?.response?.data?.message ??
          "Something went wrong while schedule a showroom visit"
      );
      dispatch(getLeadActionError());
    }
    setUpdating(false);
  };

  const requestCallBack = async (
    data: CreateRequestACallbackt,
    leadId?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);

    try {
      const response = await leadService.requestCallBack(data, leadId);

      if (response.status === StatusCodes.OK) {
        dispatch(getLeadActionSuccess({ leadActions: leadState?.leadActions }));
        if (onSuccess) onSuccess();
      } else {
        dispatch(getLeadActionError());
      }
    } catch (error) {
      toastr.error(
        "Error",
        error.response?.data?.data?.description ??
          error?.response?.data?.message ??
          "Something went wrong while requesting a call back"
      );

      dispatch(getLeadActionError());
    }
    setUpdating(false);
  };

  const convertAsEnquiry = async (
    status: string,
    id?: string,
    onSuccess?: () => void,
    onError?: (e: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.convertAsEnquiry(status, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        onError?.(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      onError?.(error);
    }
    setUpdating(false);
  };

  const convertAsVSLEnquiry = async (
    status: string,
    id?: string,
    quality?: string,
    onSuccess?: () => void,
    onError?: (e: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.convertAsVSLEnquiry(
        status,
        id,
        quality
      );

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        onError?.(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      onError?.(error);
    }
    setUpdating(false);
  };

  const deleteLead = async (
    id?: string,
    onSuccess?: () => void,
    onError?: (e: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.deleteLead(id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        onError?.(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      onError?.(error);
    }
    setUpdating(false);
  };

  const getLeadAction = async (id?: string, onSuccess?: () => void) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(getLeadActionRequest());
    try {
      const response = await leadService.getLeadAction(id);

      if (response.status === StatusCodes.OK) {
        dispatch(getLeadActionSuccess({ leadActions: response.data }));
        if (onSuccess) onSuccess();
      } else {
        dispatch(getLeadActionError());
      }
    } catch (error) {
      dispatch(getLeadActionError());
    }
    setUpdating(false);
  };

  const updateLead = async (
    data: CreateLead,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateLead(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      setLeadError(error?.response?.data?.message);

      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateTags = async (data: Tag, id?: string, onSuccess?: () => void) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateTags(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateLeadTitle = async (
    title?: string,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateLeadTitle(title, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      setLeadError(error?.response?.data?.message);

      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateSetClosingDate = async (
    date: Date,
    id?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateSetClosingDate(date, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      setLeadError(error?.response?.data?.message);

      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateShowroom = async (
    id?: string,
    showroomId?: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateShowroom(id, showroomId);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      if (error?.response?.data?.data?.description) {
        toastr.error("Error", error?.response?.data?.data?.description);
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const markAsLost = async (
    id: string,
    data: IMarkAsLost,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.markAsLost(id, data);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...response.data,
              isEditable: leadState.leadDetails?.isEditable,
              customer: leadState.leadDetails?.customer,
              configurations: leadState.leadDetails?.configurations,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        toastr.error("Error", "Unable to mark as lost");
      }
    } catch (error) {
      toastr.error("Error", error?.response?.data?.message);
    }
    setUpdating(false);
  };

  const clearData = async () => {
    dispatch(clearLeadAction());
  };

  const acceptMarkAsLost = async (
    id: string,
    data: { comment?: string },
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.acceptMarkAsLost(id, data);

      if (response.status === StatusCodes.OK) {
        if (onSuccess) onSuccess();
      } else {
        toastr.error("Error", "Unable to mark as lost");
      }
    } catch (error) {
      toastr.error("Error", error?.response?.data?.message);
    }
    setUpdating(false);
  };

  const rejectMarkAsLost = async (
    id: string,
    data: { comment?: string },
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.rejectMarkAsLost(id, data);

      if (response.status === StatusCodes.OK) {
        if (onSuccess) onSuccess();
      } else {
        toastr.error("Error", "Unable to mark as lost");
      }
    } catch (error) {
      toastr.error("Error", error?.response?.data?.message);
    }
    setUpdating(false);
  };

  const updateUsers = async (
    addUsers: string[],
    removeUsers: string[],
    id?: string,
    onSuccess?: () => void,
    onError?: (e: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateCompanyUsers(
        addUsers,
        removeUsers,
        id
      );

      if (response.status === StatusCodes.OK) {
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        onError?.(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      onError?.(error);
    }
    setUpdating(false);
  };
  const updateLeadDetails = async (data: Lead, onSuccess?: () => void) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      dispatch(
        updateLeadAction({
          leadDetails: data,
        })
      );
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateCompanyDetails = async (
    data: Company,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      dispatch(
        updateLeadAction({
          leadDetails: {
            ...leadState.leadDetails,
            company: data,
          },
        })
      );
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const updateCompanyDemographics = async (
    data: Company,
    id: string,
    onSuccess?: () => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.updateCompanyDemographics(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              company: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
      }
    } catch (error) {
      if (error?.response?.data?.data?.message) {
        toastr.error("Error", error?.response?.data?.data?.message.join());
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
      dispatch(updateLeadErrorAction());
    }
    setUpdating(false);
  };

  const assignCompanySalesPerson = async (
    data: {
      assignedPersonId: string;
    },
    id?: string,
    onSuccess?: () => void,
    onError?: (e: any) => void
  ) => {
    setLeadError(undefined);
    setUpdating(true);
    dispatch(updateLeadRequestAction());
    try {
      const response = await leadService.assignCompanySalesPerson(data, id);

      if (response.status === StatusCodes.OK) {
        dispatch(
          updateLeadAction({
            leadDetails: {
              ...leadState.leadDetails,
              company: response.data,
            },
          })
        );
        if (onSuccess) onSuccess();
      } else {
        dispatch(updateLeadErrorAction());
        onError?.(response);
      }
    } catch (error) {
      dispatch(updateLeadErrorAction());
      onError?.(error);
    }
    setUpdating(false);
  };

  return {
    leadError,
    updating,
    createLead,
    updateSocialMediaDetails,
    updatePreferredTimes,
    updateVehicleDetails,
    updatePartnerInfo,
    updateBuyingInfluences,
    updateCustomerAnswers,
    getLead,
    updateWorkExperiences,
    bulkAssign,
    updateDocumentsDetails,
    updateDemographics,
    updateCustomerDetails,
    resetWorkExperiences,
    assignCrmAgent,
    assignLeadAssigneeHead,
    updateAssignee,
    assignSalesPerson,
    addVehicle,
    updateLeadQuality,
    scheduleTestDrive,
    scheduleShowroomVisit,
    requestCallBack,
    convertAsEnquiry,
    convertAsVSLEnquiry,
    getLeadAction,
    updateLead,
    updateTags,
    updateLeadTitle,
    updateSetClosingDate,
    updateVehicle,
    setVehicleAsPrimary,
    updateShowroom,
    markAsLost,
    clearData,
    acceptMarkAsLost,
    rejectMarkAsLost,
    fetching,
    updateUsers,
    updateLeadDetails,
    updateCompanyDetails,
    updateCompanyDemographics,
    deleteLead,
    updateCompanySocialMediaDetails,
    addInterestedProject,
    updateInterestedProject,
    assignCompanySalesPerson,
  };
};
